// ============================================================================
// Every write the Subcontractors hub makes, in one place (item #683).
//
// ⛔ One door (TCSD-0188 / 0368): a sub is only ever CREATED by
// add_subcontractor(). Status moves are a plain edit of the sub's own record —
// any employee may (TCSD-0170, TCSD-0373 ¶3); the audit trail records who.
// Every edit goes through commitWrite so a zero-row write is a failure, not a
// silent no-op (TCSD-0168).
// ============================================================================

import { supabase } from '../../../shared/supabase';
import { commitWrite, refreshReaders } from '../../../shared/save-path';
import { rpcErrorMessage } from '../../../shared/rpc-error';
import { mintProspectIntakeUrl } from '../../sourcing/hooks/use-sourcing-data';
import { NIGHTLY_DIVISIONS, PROJECT_DIVISIONS, type PipelineStage } from '../stages';
import { alreadyOnFileFrom } from '../already-on-file';

/** What a sub does: nightly cleaning, project work, or both (item 869). */
export type WorkKind = 'nightly' | 'projects' | 'both';

export function divisionsFor(kind: WorkKind): string[] {
  if (kind === 'both') return [...NIGHTLY_DIVISIONS, ...PROJECT_DIVISIONS];
  return kind === 'nightly' ? [...NIGHTLY_DIVISIONS] : [...PROJECT_DIVISIONS];
}

/** The two switches on + Add → the kind of work; null when neither is on. */
export function workKindFrom(nightly: boolean, projects: boolean): WorkKind | null {
  if (nightly && projects) return 'both';
  if (nightly) return 'nightly';
  if (projects) return 'projects';
  return null;
}

/** How the kind of work reads everywhere but the + Add sheet (Ben, 2026-10-14). */
export function workLabel(doesNightly: boolean, doesProjects: boolean): string | null {
  if (doesNightly && doesProjects) return 'Nightly + project work';
  if (doesNightly) return 'Nightly';
  if (doesProjects) return 'Project work';
  return null;
}

export async function addSubcontractor(args: {
  name: string;
  phone: string;
  markets: string[];
  kind: WorkKind;
  status: PipelineStage;
  email?: string | null;
  channel?: 'AM' | 'Discovery';
  discoverySourceId?: string | null;
  /** The second press after "Already on file?": it's a different company. */
  addAnyway?: boolean;
}): Promise<{ provider_id: string; prospect_id: string }> {
  const { data, error } = await supabase.rpc('add_subcontractor', {
    p_name: args.name,
    p_phone: args.phone,
    p_coverage_markets: args.markets,
    p_capabilities: divisionsFor(args.kind),
    p_status: args.status,
    p_email: args.email ?? undefined,
    p_channel: args.channel ?? 'AM',
    p_discovery_source_id: args.discoverySourceId ?? undefined,
    p_add_anyway: args.addAnyway ?? false,
  });
  if (error) {
    const found = alreadyOnFileFrom(error);
    if (found) throw found;
    throw new Error(rpcErrorMessage(error, 'Could not add the subcontractor.'));
  }
  const out = data as { provider_id?: string; prospect_id?: string } | null;
  if (!out?.provider_id || !out.prospect_id) throw new Error('The new subcontractor came back without an id.');
  return { provider_id: out.provider_id, prospect_id: out.prospect_id };
}

/** The sub's own application link — fills in their existing record (TCSD-0370 ¶2). */
export async function applicationLinkFor(providerId: string): Promise<string> {
  const { data, error } = await supabase
    .from('sourcing_prospects')
    .select('prospect_id')
    .eq('promoted_provider_id', providerId)
    .order('created_at', { ascending: false })
    .limit(1);
  if (error) throw new Error(rpcErrorMessage(error, 'Could not find the application record.'));
  const pid = (data?.[0] as { prospect_id?: string } | undefined)?.prospect_id;
  if (!pid) throw new Error('This sub has no application record to send.');
  return mintProspectIntakeUrl(pid);
}

/** Open the phone's Messages app with the link ready to send. Nothing is sent by the app. */
export function openText(phone: string | null, body: string): void {
  const to = (phone ?? '').replace(/[^0-9+]/g, '');
  window.location.href = `sms:${to}?&body=${encodeURIComponent(body)}`;
}

export async function setSubStatus(providerId: string, status: string, extra: Record<string, unknown> = {}) {
  await commitWrite({
    label: 'change the subcontractor’s status',
    write: supabase
      .from('service_providers')
      .update({ provider_status: status, ...extra })
      .eq('provider_id', providerId)
      .select('provider_id'),
    touches: ['service_providers'],
  });
}

/** What the work record says a sub does (v_subcontractor_work). */
export interface SubWork {
  does_nightly: boolean;
  kind_source: string;
}

/** The bench is for nightly cleaners (Ben, 2026-10-03): refused when the work on
 *  record is project work only. ONE copy — the pipeline board and Ask AI. */
export function benchRefusal(work: SubWork | null): string | null {
  return work && work.kind_source !== 'unknown' && !work.does_nightly
    ? 'The bench is for nightly cleaners — their work is project work only.'
    : null;
}

/** A pipeline move's extra fields: going to the bench with no work on record
 *  records them as nightly. */
export function pipelineMoveExtra(next: string, work: SubWork | null): Record<string, unknown> {
  return next === 'Ready' && (!work || work.kind_source === 'unknown') ? { capabilities: divisionsFor('nightly') } : {};
}

/** The pipeline board's move (Lead / Interviewing / Bench), on the sub as it is now. */
export async function moveSubOnPipeline(providerId: string, next: string): Promise<void> {
  const { data, error } = await supabase
    .from('v_subcontractor_work')
    .select('does_nightly, kind_source')
    .eq('provider_id', providerId)
    .maybeSingle();
  if (error) throw new Error(rpcErrorMessage(error, 'Could not read what work they do.'));
  const work = (data as SubWork | null) ?? null;
  const refused = next === 'Ready' ? benchRefusal(work) : null;
  if (refused) throw new Error(refused);
  await setSubStatus(providerId, next, pipelineMoveExtra(next, work));
}

/** Make a sub Tier 1, or take it away (set_subcontractor_tier_1). */
export async function setSubTier1(providerId: string, tier1: boolean): Promise<void> {
  const { error } = await supabase.rpc('set_subcontractor_tier_1', { p_provider_id: providerId, p_tier_1: tier1 });
  if (error) throw new Error(rpcErrorMessage(error, tier1 ? 'Could not make that sub Tier 1.' : 'Could not remove Tier 1.'));
  refreshReaders(['service_providers']);
}

export async function setSubKind(providerId: string, kind: WorkKind) {
  await commitWrite({
    label: 'record the kind of work',
    write: supabase
      .from('service_providers')
      .update({ capabilities: divisionsFor(kind) })
      .eq('provider_id', providerId)
      .select('provider_id'),
    touches: ['service_providers'],
  });
}

export async function setSubMarkets(providerId: string, markets: string[]) {
  await commitWrite({
    label: 'change the subcontractor’s markets',
    write: supabase
      .from('service_providers')
      .update({ coverage_markets: markets.length ? markets : null })
      .eq('provider_id', providerId)
      .select('provider_id'),
    touches: ['service_providers'],
  });
}

/** Return to bench: Inactive -> Ready, with the one required line kept in the
 *  sub's own pipeline notes (TCSD-0373 ¶5: existing note, no new column). */
export async function returnToBench(providerId: string, why: string, who: string | null) {
  const line = why.trim();
  if (!line) throw new Error('One line on why they are coming back, please.');
  const { data, error } = await supabase
    .from('service_providers')
    .select('provider_status, sourcing_notes')
    .eq('provider_id', providerId)
    .single();
  if (error) throw new Error(rpcErrorMessage(error, 'Could not read the subcontractor.'));
  const row = data as { provider_status: string; sourcing_notes: string | null };
  if (row.provider_status !== 'Inactive') throw new Error('Only an inactive sub can be returned to the bench.');
  // The bench is nightly cleaners only — the same rule the Bench answer on
  // Need a status applies: refused when their work on record is project work
  // only; with no work on record, coming back to the bench records them as nightly.
  const { data: work, error: workErr } = await supabase
    .from('v_subcontractor_work')
    .select('does_nightly, kind_source')
    .eq('provider_id', providerId)
    .maybeSingle();
  if (workErr) throw new Error(rpcErrorMessage(workErr, 'Could not read what work they do.'));
  const w = work as { does_nightly: boolean; kind_source: string } | null;
  if (w && w.kind_source !== 'unknown' && !w.does_nightly) {
    throw new Error('The bench is for nightly cleaners — their work on record is project work only.');
  }
  const stamp = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Chicago' });
  const note = `${stamp} returned to bench${who ? ` by ${who}` : ''}: ${line}`;
  await setSubStatus(providerId, 'Ready', {
    sourcing_notes: row.sourcing_notes ? `${row.sourcing_notes}\n${note}` : note,
    ...(!w || w.kind_source === 'unknown' ? { capabilities: divisionsFor('nightly') } : {}),
  });
}

export async function startPaperwork(providerId: string): Promise<{ created: boolean; message: string }> {
  const { data, error } = await supabase.rpc('start_sub_paperwork', { p_provider_id: providerId });
  if (error) throw new Error(rpcErrorMessage(error, 'Could not start the paperwork.'));
  const out = data as { created?: boolean; message?: string } | null;
  return { created: Boolean(out?.created), message: out?.message ?? '' };
}

export async function markInactive(providerId: string, eid: string | null) {
  const { error } = await supabase.rpc('deactivate_service_provider', {
    p_provider_id: providerId,
    p_created_by_eid: eid ?? undefined,
  });
  if (error) throw new Error(rpcErrorMessage(error, 'Could not mark the subcontractor inactive.'));
}
